<template>
  <div class="page-container" v-if="settlement">
    <div class="page-title-container d-flex align-items-center justify-content-between">
      <div>
        <h1 class="page-title mb-0">
          {{ settlement.settlement_code }}
          <span :class="`badge ml-2 ${statusMeta.color}`">{{ statusMeta.label }}</span>
        </h1>
        <div class="text-muted small mt-1">
          {{ settlement.provider_name }} · {{ settlement.period_start }} al {{ settlement.period_end }}
        </div>
      </div>
      <router-link :to="{ name: 'settlements-list' }" class="button button-light">Volver</router-link>
    </div>

    <!-- Línea de tiempo del flujo -->
    <div class="flow-steps">
      <div v-for="(s, i) in flowSteps" :key="s.key" class="flow-step" :class="stepClass(i)">
        <span class="flow-dot">{{ i + 1 }}</span>
        <div>
          <div class="flow-label">{{ s.label }}</div>
          <div class="flow-date">{{ s.date || "—" }}</div>
        </div>
      </div>
    </div>

    <!-- Viajes -->
    <div class="table-container">
      <div class="table-header">
        <h3 class="m-0">Viajes de la liquidación ({{ settlement.total_trips }})</h3>
      </div>
      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-4">#</th>
              <th class="text-center">Fecha</th>
              <th class="text-center">Código</th>
              <th>Ruta</th>
              <th>Vehículo / Conductor</th>
              <th class="text-center">Tarifa</th>
              <th class="text-center">Extras</th>
              <th class="text-center">Total</th>
            </tr>
          </thead>
          <tbody v-for="(t, i) in settlement.trips" :key="t.booking_id">
            <tr :class="{ 'has-extras': t.extras.length }" @click="toggleExtras(t.booking_id)">
              <td class="pl-4">{{ i + 1 }}</td>
              <td class="text-center">
                <div>{{ t.fecha }}</div>
                <div class="text-muted small">{{ t.hora }}</div>
              </td>
              <td class="text-center font-weight-bold">{{ t.ride }}</td>
              <td>
                <div>{{ t.origen }}</div>
                <div class="text-muted small">{{ t.destino }}</div>
              </td>
              <td>
                <div>{{ t.vehicle_plate || "—" }}</div>
                <div class="text-muted small">{{ t.driver_name || "—" }}</div>
              </td>
              <td class="text-center">{{ formatAmount(t.fare_amount) }}</td>
              <td class="text-center">
                <span v-if="t.extras.length" class="extras-toggle">
                  <i :class="expanded[t.booking_id] ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                  {{ formatAmount(t.extras_amount) }}
                </span>
                <span v-else>—</span>
              </td>
              <td class="text-center font-weight-bold">{{ formatAmount(t.total_amount) }}</td>
            </tr>
            <tr v-if="expanded[t.booking_id]" class="extras-row">
              <td colspan="8">
                <div v-for="e in t.extras" :key="e.id" class="extra-line">
                  <span class="extra-type">{{ extraTypeLabel(e.extra_type) }}</span>
                  <span v-if="e.description" class="text-muted">{{ e.description }}</span>
                  <span class="ml-auto">{{ e.currency }} {{ formatAmount(e.amount) }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!settlement.trips.length" class="table-no-results">
          <p class="m-0 px-3">Sin viajes.</p>
        </div>
      </div>
    </div>

    <!-- Resumen y acciones -->
    <div class="summary-actions">
      <div class="summary-box">
        <div class="summary-line">
          <span>Subtotal tarifas</span>
          <span>{{ settlement.currency }} {{ formatAmount(settlement.total_fare_amount) }}</span>
        </div>
        <div class="summary-line">
          <span>Extras</span>
          <span>{{ settlement.currency }} {{ formatAmount(settlement.total_extras_amount) }}</span>
        </div>
        <div class="summary-line text-danger">
          <span>Deducciones</span>
          <span>- {{ settlement.currency }} {{ formatAmount(settlement.deductions_amount) }}</span>
        </div>
        <div v-if="settlement.deduction_notes" class="deduction-notes">
          {{ settlement.deduction_notes }}
        </div>
        <div class="summary-line summary-total">
          <span>Total a pagar</span>
          <span>{{ settlement.currency }} {{ formatAmount(settlement.final_amount) }}</span>
        </div>
      </div>

      <div class="actions-box">
        <div class="download-buttons mb-2">
          <button type="button" class="button button-light" @click="download">
            <i class="ri-file-excel-2-line"></i> Excel
          </button>
          <button type="button" class="button button-light" @click="downloadPdf">
            <i class="ri-file-pdf-2-line"></i> PDF
          </button>
        </div>

        <template v-if="isDraft">
          <button type="button" class="button button-light w-100 mb-2" @click="openDeductions">
            <i class="ri-edit-line"></i> Editar deducciones
          </button>
          <button type="button" class="button button-primary w-100 mb-2" @click="send">
            <i class="ri-send-plane-line"></i> Enviar al proveedor
          </button>
          <button type="button" class="button button-delete w-100" @click="confirmDelete">
            <i class="ri-delete-bin-line"></i> Eliminar
          </button>
        </template>

        <template v-else-if="settlement.settlement_status === 'SENT'">
          <button type="button" class="button button-primary w-100 mb-2" @click="openConfirm">
            <i class="ri-check-line"></i> Registrar confirmación
          </button>
          <button type="button" class="button button-light w-100" @click="returnToDraft">
            <i class="ri-arrow-go-back-line"></i> Volver a borrador
          </button>
        </template>

        <template v-else-if="settlement.settlement_status === 'CONFIRMED'">
          <button type="button" class="button button-primary w-100" @click="openPay">
            <i class="ri-money-dollar-circle-line"></i> Registrar pago
          </button>
        </template>

        <div v-else class="paid-info">
          <div><strong>Pagado</strong> el {{ settlement.paid_at }}</div>
          <div>Método: {{ settlement.payment_method }}</div>
          <div v-if="settlement.confirmed_by_name">Confirmó: {{ settlement.confirmed_by_name }}</div>
          <div v-if="!settlement.has_payment_proof" class="text-muted small mt-1">Sin comprobante adjunto.</div>
        </div>
      </div>
    </div>

    <!-- Deducciones -->
    <b-modal id="deductions-modal" title="Editar deducciones" hide-footer size="sm">
      <div class="custom-form">
        <div class="form-group">
          <label class="label">Monto a deducir</label>
          <input type="number" step="0.01" min="0" class="custom-form-control" v-model.number="deductionForm.amount" />
        </div>
        <div class="form-group">
          <label class="label">Motivo</label>
          <textarea class="custom-form-control" rows="3" v-model="deductionForm.notes" maxlength="500"
            placeholder="Adelantos, multas, ajustes..."></textarea>
        </div>
        <div class="d-flex justify-content-end">
          <b-button variant="none" class="button button-light mr-2" @click="$bvModal.hide('deductions-modal')">
            Cancelar
          </b-button>
          <b-button variant="none" class="button button-primary" @click="saveDeductions">Guardar</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Confirmación del proveedor -->
    <b-modal id="confirm-modal" title="Registrar confirmación" hide-footer size="sm">
      <div class="custom-form">
        <div class="form-group">
          <label class="label">¿Quién confirmó? *</label>
          <input type="text" class="custom-form-control" v-model="confirmName" maxlength="200"
            placeholder="Nombre de quien validó del lado del proveedor" />
        </div>
        <div class="d-flex justify-content-end">
          <b-button variant="none" class="button button-light mr-2" @click="$bvModal.hide('confirm-modal')">
            Cancelar
          </b-button>
          <b-button variant="none" class="button button-primary" :disabled="!confirmName.trim()" @click="confirm">
            Confirmar
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Pago -->
    <b-modal id="pay-modal" title="Registrar pago" hide-footer size="sm">
      <div class="custom-form">
        <div class="form-group">
          <label class="label">Método de pago *</label>
          <select class="custom-form-control" v-model="payForm.method">
            <option v-for="m in paymentMethods" :key="m.key" :value="m.key">{{ m.label }}</option>
          </select>
        </div>
        <div class="form-group">
          <label class="label">Comprobante <span class="text-muted small">(PDF, JPG o PNG; opcional)</span></label>
          <input type="file" class="custom-form-control" accept=".pdf,.jpg,.jpeg,.png" @change="onProofChange" />
        </div>
        <div class="d-flex justify-content-end">
          <b-button variant="none" class="button button-light mr-2" @click="$bvModal.hide('pay-modal')">
            Cancelar
          </b-button>
          <b-button variant="none" class="button button-primary" :disabled="paying" @click="pay">
            {{ paying ? "Guardando..." : "Registrar pago" }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import { Alert, Util, ConfirmationModal, ConfirmationModalMixin } from "wize-admin";

import { SettlementService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

// Detalle de una liquidación (DEF-ADM-021 §5.2). Las acciones disponibles
// dependen del estado: borrador, enviada, confirmada o pagada.
export default {
  name: "SettlementDetail",
  components: { ConfirmationModal },
  mixins: [ConfirmationModalMixin],
  data() {
    return {
      settlement: null,
      expanded: {},
      deductionForm: { amount: 0, notes: "" },
      confirmName: "",
      payForm: { method: "TRANSFERENCIA", proof: null },
      paying: false,
    };
  },
  computed: {
    settlementId() {
      return this.$route.params.id;
    },
    isDraft() {
      return this.settlement.settlement_status === "DRAFT";
    },
    statusMeta() {
      return (
        Util.searchValue(this.settlement.settlement_status, LocalConstants.SettlementStatus) || {
          label: this.settlement.settlement_status,
          color: "badge-gray",
        }
      );
    },
    paymentMethods() {
      return Object.values(LocalConstants.SettlementPaymentMethod);
    },
    flowSteps() {
      const s = this.settlement;
      return [
        { key: "DRAFT", label: "Borrador", date: s.created_at },
        { key: "SENT", label: "Enviada", date: s.sent_at },
        { key: "CONFIRMED", label: "Confirmada", date: s.confirmed_at },
        { key: "PAID", label: "Pagada", date: s.paid_at },
      ];
    },
  },
  methods: {
    formatAmount(v) {
      return Number(v || 0).toFixed(2);
    },
    extraTypeLabel(key) {
      const meta = LocalConstants.ProviderExtraType[key];
      return meta ? meta.label : key;
    },
    // Un paso está completo si ya tiene fecha; el actual es el último con fecha.
    stepClass(index) {
      const done = this.flowSteps.filter((s) => s.date).length;
      if (index < done - 1) return "step-done";
      if (index === done - 1) return "step-current";
      return "step-pending";
    },
    toggleExtras(bookingId) {
      this.$set(this.expanded, bookingId, !this.expanded[bookingId]);
    },
    async load() {
      try {
        await this.$store.dispatch("app/loading", true);
        const res = await SettlementService.get(this.settlementId);
        this.settlement = res.payload;
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo cargar la liquidación");
        this.$router.push({ name: "settlements-list" });
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    openDeductions() {
      this.deductionForm = {
        amount: this.settlement.deductions_amount || 0,
        notes: this.settlement.deduction_notes || "",
      };
      this.$bvModal.show("deductions-modal");
    },
    async saveDeductions() {
      try {
        await SettlementService.update(this.settlementId, {
          deductions_amount: Number(this.deductionForm.amount) || 0,
          deduction_notes: this.deductionForm.notes || null,
        });
        Alert.success("Deducciones actualizadas");
        this.$bvModal.hide("deductions-modal");
        await this.load();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudieron guardar las deducciones");
      }
    },
    async send() {
      await this.runAction(() => SettlementService.send(this.settlementId), "Liquidación enviada");
    },
    async returnToDraft() {
      await this.runAction(
        () => SettlementService.returnToDraft(this.settlementId),
        "La liquidación volvió a borrador"
      );
    },
    openConfirm() {
      this.confirmName = "";
      this.$bvModal.show("confirm-modal");
    },
    async confirm() {
      this.$bvModal.hide("confirm-modal");
      await this.runAction(
        () => SettlementService.confirm(this.settlementId, this.confirmName.trim()),
        "Confirmación registrada"
      );
    },
    openPay() {
      this.payForm = { method: "TRANSFERENCIA", proof: null };
      this.$bvModal.show("pay-modal");
    },
    onProofChange(ev) {
      this.payForm.proof = ev.target.files[0] || null;
    },
    async pay() {
      this.paying = true;
      try {
        const data = new FormData();
        data.append("payment_method", this.payForm.method);
        if (this.payForm.proof) data.append("proof", this.payForm.proof);
        await SettlementService.pay(this.settlementId, data);
        Alert.success("Pago registrado");
        this.$bvModal.hide("pay-modal");
        await this.load();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo registrar el pago");
      } finally {
        this.paying = false;
      }
    },
    confirmDelete() {
      this.openConfirmationModal({
        title: "Eliminar liquidación",
        description:
          "¿Seguro que quieres eliminarla? Sus viajes vuelven a quedar disponibles para liquidar.",
        acceptText: "Eliminar",
        action: "delete",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "delete") this.remove();
    },
    async remove() {
      try {
        await SettlementService.delete(this.settlementId);
        Alert.success("Liquidación eliminada");
        this.$router.push({ name: "settlements-list" });
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo eliminar la liquidación");
      }
    },
    async download() {
      await this.saveFile(
        () => SettlementService.download(this.settlementId),
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "xlsx"
      );
    },
    async downloadPdf() {
      await this.saveFile(
        () => SettlementService.downloadPdf(this.settlementId),
        "application/pdf",
        "pdf"
      );
    },
    // Descarga el archivo que devuelve el backend con el código de la
    // liquidación como nombre. Lo usan el Excel y el PDF.
    async saveFile(request, type, extension) {
      try {
        await this.$store.dispatch("app/loading", true);
        const response = await request();
        const blob = new Blob([response.data], { type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.settlement.settlement_code}.${extension}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (ex) {
        console.error(ex);
        Alert.error("No se pudo descargar el archivo");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    // Envoltorio de las acciones que solo cambian de estado.
    async runAction(fn, successMessage) {
      try {
        await fn();
        Alert.success(successMessage);
        await this.load();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo completar la acción");
      }
    },
  },
  async mounted() {
    await this.load();
  },
};
</script>

<style lang="stylus" scoped>
.flow-steps
  display flex
  gap 8px
  flex-wrap wrap
  margin-bottom 16px
.flow-step
  display flex
  align-items center
  gap 8px
  background #fff
  border 1px solid #e5e7eb
  border-radius 10px
  padding 8px 14px
  flex 1
  min-width 160px
.flow-dot
  width 24px
  height 24px
  border-radius 50%
  display flex
  align-items center
  justify-content center
  font-size 12px
  font-weight 600
  background #e9ecef
  color #6c757d
.flow-label
  font-weight 600
  font-size 13px
.flow-date
  font-size 11px
  color #6c757d
.step-done .flow-dot
  background #28a745
  color #fff
.step-current
  border-color #007bff
  .flow-dot
    background #007bff
    color #fff
.summary-actions
  display flex
  gap 16px
  flex-wrap wrap
  margin-top 16px
.summary-box
  flex 1
  min-width 280px
  background #fff
  border 1px solid #e5e7eb
  border-radius 10px
  padding 16px
.summary-line
  display flex
  justify-content space-between
  padding 4px 0
.summary-total
  border-top 1px solid #e5e7eb
  margin-top 8px
  padding-top 10px
  font-weight 700
  font-size 17px
.deduction-notes
  font-size 12px
  color #6c757d
  padding-bottom 4px
.actions-box
  width 260px
  background #fff
  border 1px solid #e5e7eb
  border-radius 10px
  padding 16px
// Excel y PDF lado a lado, a medio ancho cada uno.
.download-buttons
  display flex
  gap 8px
  .button
    flex 1
.paid-info
  font-size 13px
  color #495057
.has-extras
  cursor pointer
.extras-toggle
  color #007bff
.extras-row td
  background #f8f9fa
.extra-line
  display flex
  gap 10px
  align-items center
  padding 3px 0 3px 40px
  font-size 13px
.extra-type
  font-weight 600
</style>
